import { FC, useMemo } from "react";
import moment from "moment";
import { startCase } from "lodash";

import { Heading, Table, Text, type TableColumn } from "@/ui";
import { ActivityLog, IDataPaneProps } from "@/types";
import { NOT_AVAILABLE } from "@/config";
import { OrderPaneLoading } from "./OrderPaneLoading";
import styles from "./ActivityLogPane.module.scss";

type ActivityRow = ActivityLog & {
    index: number;
};

const cellText = (value?: string | null) => (
    <Text as="span" variant="body-md">
        {value || NOT_AVAILABLE}
    </Text>
);

type ActivityLogTableProps = {
    activityLogs?: Array<ActivityLog | null> | null;
    loading?: boolean;
};

export const ActivityLogTable: FC<ActivityLogTableProps> = ({
    activityLogs,
    loading,
}) => {
    const rows: ActivityRow[] = useMemo(
        () =>
            (activityLogs ?? [])
                .filter((log): log is ActivityLog => Boolean(log))
                .map((log, index) => ({ ...log, index })),
        [activityLogs],
    );

    const columns: TableColumn<ActivityRow>[] = [
        {
            id: "action",
            header: "Action",
            minWidth: 140,
            renderCell: row => cellText(row.action),
        },
        {
            id: "status",
            header: "Status",
            minWidth: 120,
            renderCell: row =>
                cellText(row.status ? startCase(row.status) : null),
        },
        {
            id: "user",
            header: "Updated By",
            minWidth: 120,
            renderCell: row => cellText(row.user),
        },
        {
            id: "timestamp",
            header: "Timestamp",
            minWidth: 220,
            renderCell: row =>
                cellText(
                    row.timestamp
                        ? moment(row.timestamp).format(
                              "ddd, MMM Do, YYYY - h:mm:ss a",
                          )
                        : null,
                ),
        },
    ];

    return (
        <div className={styles.tableWrap}>
            <Table
                columns={columns}
                emptyMessage={
                    loading ? "Loading activity…" : "No activity logs"
                }
                getRowId={row => String(row.index)}
                rows={rows}
                showEmptyGraphic={false}
            />
        </div>
    );
};

const ActivityLogPane: FC<IDataPaneProps> = ({ order, loading }) => {
    if (loading) {
        return <OrderPaneLoading />;
    }

    return (
        <div className={styles.root}>
            <Heading className={styles.title} level={5}>
                Activity logs
            </Heading>
            <ActivityLogTable
                activityLogs={order?.activity_logs}
                loading={loading}
            />
        </div>
    );
};

export default ActivityLogPane;
